import * as React from 'react'
import {
  useUpdateManualNodeMutation,
  type OntologyGraphNode
} from '@axilon-apps/domains-ontology-ui-state'

import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../components/ui/dialog'
import { Label } from '../../components/ui/label'
import { Textarea } from '../../components/ui/textarea'

const SYSTEM_KEYS = new Set(['_id', 'label', 'valid', 'metadata', 'provenance'])

function extractEditableProps(node: OntologyGraphNode): string {
  const editable: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(node)) {
    if (!SYSTEM_KEYS.has(k)) {
      editable[k] = v
    }
  }
  return JSON.stringify(editable, null, 2)
}

interface Props {
  open: boolean
  node: OntologyGraphNode
  onClose: () => void
  onSuccess: () => void
}

const NodeEditModal = ({ open, node, onClose, onSuccess }: Props) => {
  const [propsJson, setPropsJson] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [jsonError, setJsonError] = React.useState<string | null>(null)
  const [updateNode, { isLoading, error }] = useUpdateManualNodeMutation()

  React.useEffect(() => {
    if (open) {
      setPropsJson(extractEditableProps(node))
      setNotes('')
      setJsonError(null)
    }
  }, [open, node])

  const handleSave = async () => {
    let properties: Record<string, unknown>
    try {
      properties = JSON.parse(propsJson) as Record<string, unknown>
      setJsonError(null)
    } catch {
      setJsonError('Invalid JSON — please fix before saving.')
      return
    }

    await updateNode({ id: node._id, properties, notes: notes || undefined }).unwrap()
    onSuccess()
    onClose()
  }

  const nodeName = String(node.name ?? node.id ?? node._id)

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
    >
      <DialogContent className="sm:max-w-160">
        <DialogHeader>
          <DialogTitle className="flex flex-row items-center gap-2">
            <span>Edit Node</span>
            <Badge className="border border-line bg-transparent text-ink-2">{node.label}</Badge>
            <span className="text-sm font-normal text-ink-3">{nodeName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 pt-1">
          {error ? (
            <div className="rounded-md bg-st-down-bg p-2 text-sm text-st-down">
              Failed to save changes.
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="node-edit-props">Properties (JSON)</Label>
            <Textarea
              id="node-edit-props"
              rows={8}
              value={propsJson}
              onChange={(e) => setPropsJson(e.target.value)}
              aria-invalid={Boolean(jsonError) || undefined}
              className="font-mono text-xs"
            />
            {jsonError ? <p className="m-0 text-xs text-st-down">{jsonError}</p> : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="node-edit-notes">Notes (optional)</Label>
            <Textarea
              id="node-edit-notes"
              rows={2}
              placeholder="Reason for this change…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={isLoading}
            className="text-ink-2 hover:text-ink"
          >
            Cancel
          </Button>
          <Button disabled={isLoading} onClick={() => void handleSave()}>
            {isLoading ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default NodeEditModal
